import React, { useMemo, useState } from 'react';

import moment from 'moment';

import useStoreTranslation from 'hooks/useStoreTranslation';
import { AirFareConditions } from 'models/flightValidation';
import CancellationPolicies from 'screens/components/TravelDetails/components/FlightDetailsSection/components/CancellationPolicies';
import { getFlightAirStretchText } from 'utils/generic';

import {
  Checkbox,
  Container,
  Field,
  Fields,
  FlexRight,
  IconFlights,
  Place,
  SearchButton,
  SearchButtonContainer,
  Subtitle,
  Title,
  SectionContainer,
} from './styles';


interface Props {
  departureCity?: string;
  departureDate?: Date;
  arrivalDate?: Date;
  departureCode?: string;
  arrivalCode?: string;
  onContinue: (changeDeparture: boolean, changeArrival: boolean) => void;
  defaultChangeDeparture?: boolean;
  defaultChangeArrival?: boolean;
  airFareConditions?: AirFareConditions[];
  arrivalCity?: string;
  travelAirStretch: string;
}

const FlightsExchangeOption = ({
  departureCity,
  departureDate,
  arrivalDate,
  departureCode,
  arrivalCode,
  onContinue,
  defaultChangeDeparture,
  defaultChangeArrival,
  airFareConditions,
  arrivalCity,
  travelAirStretch,
}: Props) => {
  const { t } = useStoreTranslation();
  const formatDate = (date: Date) => moment.utc(date).format('ddd[,] DD MMM');
  const [changeDeparture, setChangeDeparture] = useState(defaultChangeDeparture);
  const [changeArrival, setChangeArrival] = useState(defaultChangeArrival);
  const canContinue = useMemo(() => {
    if (!changeDeparture && !changeArrival) return true;
    if (changeDeparture && changeArrival) return false;
    return !(changeDeparture || changeArrival);
  }, [changeDeparture, changeArrival]);
  return (
    <>
      <Container>
        <Title>{t('flightsExchange.option.title')}</Title>
        <Fields>
          <Field>
            <Subtitle>
              {t('flightsExchange.option.subtitle')} {arrivalCity ?? arrivalCode} - {t(getFlightAirStretchText(travelAirStretch))}
            </Subtitle>
            <FlexRight>
              <IconFlights />
            </FlexRight>
          </Field>
          <Field>
            <Place>
              <Place.Type>{t('flightsExchange.option.departure')}</Place.Type>
              <Place.Date>{departureDate && formatDate(departureDate)}</Place.Date>
              <Place.Local>
                {departureCode} &gt; {arrivalCode}
              </Place.Local>
            </Place>
            <Checkbox
              checked={!!changeDeparture}
              onChange={() => setChangeDeparture(!changeDeparture)}
              disabled={false}
            ></Checkbox>
          </Field>
          {travelAirStretch !== 'Direct' && (
            <Field>
              <Place>
                <Place.Type>{t('flightsExchange.option.arrival')}</Place.Type>
                <Place.Date>{arrivalDate && formatDate(arrivalDate)}</Place.Date>
                <Place.Local>
                  {arrivalCode} &gt; {departureCode}
                </Place.Local>
              </Place>
              <Checkbox
                checked={!!changeArrival}
                onChange={() => setChangeArrival(!changeArrival)}
                disabled={false}
              ></Checkbox>
            </Field>
          )}
        </Fields>
        <SectionContainer>
          {airFareConditions && <CancellationPolicies policies={airFareConditions} />}
        </SectionContainer>
        <SearchButtonContainer>
          <SearchButton
            disabled={canContinue}
            onClick={() => onContinue(!!changeDeparture, !!changeArrival)}
            value={t('flightsExchange.option.button')}
            testId="flight-search-submit"
          />
        </SearchButtonContainer>
      </Container>
    </>
  );
};

export default FlightsExchangeOption;
